import { httpsCallable } from 'firebase/functions';
import { useCallback, useEffect, useState } from 'react';
import { deleteAction, listActions } from '../../lib/actionOutbox';
import { functions } from '../../lib/firebase';

/**
 * 「研究利用からの除外（同意撤回）」UI 用 hook (Phase G、old2 逐語移植)。
 *
 * old2 `apps/user/src/lib/verification.ts` の `getMyVerificationCount` /
 * `withdrawMyVerifications` (httpsCallable) を v2 features 内に集約したもの。
 *
 * functions 側 (`functions/src/verification/{getMyVerificationCount,withdrawMyVerifications}.ts`)
 * は実装済:
 *   - getMyVerificationCount({ districtId }) → { count }
 *     (collectionGroup('verifications').where('participantUid','==',uid) を path 接頭辞で絞り込み)
 *   - withdrawMyVerifications({ districtId }) → { deletedCount }
 *     (同条件を 500 件ずつ batch.delete()、各削除で onVerificationDeleted trigger が
 *      Post.verificationSummary をデクリメント、audit log 記録、rate limit 1 call/min/uid)
 *
 * old2 との差分:
 *   - old2 は VerificationListPage が直接 lib 関数を呼んでいた → v2 は hook に集約
 *     (件数 subscribe + 撤回 action + state を 1 か所にまとめ、List page を薄く保つ)。
 */

/** 認証ユーザーが指定地区で送信した検証回答の件数を取得する (count callable wrapper)。 */
export async function fetchMyVerificationCount(districtId: string): Promise<number> {
  const fn = httpsCallable<{ districtId: string }, { count: number }>(
    functions,
    'getMyVerificationCount',
  );
  const res = await fn({ districtId });
  return res.data.count ?? 0;
}

/**
 * 認証ユーザーが指定地区で送信した検証回答をすべて削除する (withdraw callable wrapper)。
 * 戻り値は削除件数。
 */
export async function withdrawMyVerifications(districtId: string): Promise<number> {
  const fn = httpsCallable<{ districtId: string }, { deletedCount: number }>(
    functions,
    'withdrawMyVerifications',
  );
  const res = await fn({ districtId });
  return res.data.deletedCount ?? 0;
}

export type MyVerificationCountState =
  | { status: 'loading' }
  | { status: 'ready'; count: number }
  | { status: 'error'; error: Error };

export interface UseMyVerificationCountResult {
  state: MyVerificationCountState;
  withdrawing: boolean;
  /** 撤回完了/失敗メッセージ (old2 と同じく一覧内に表示)。 */
  message: string | null;
  /** 研究利用から除外 (= 自分の全回答を物理削除)。confirm は呼び出し側で行う想定。 */
  withdraw: () => Promise<void>;
}

/**
 * 件数を 1 回取得し、撤回 action と局所 state を提供する hook。
 *
 * - 件数取得失敗は致命ではないので count=0 扱い (old2 と同じ、UI は単に section 非表示)。
 * - withdraw 成功後は count を 0 に更新し、件数メッセージを表示する。
 */
export function useMyVerificationCount(
  districtId: string,
  uid: string | null,
  districtName: string,
): UseMyVerificationCountResult {
  const [state, setState] = useState<MyVerificationCountState>({ status: 'loading' });
  const [withdrawing, setWithdrawing] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!uid) {
      setState({ status: 'ready', count: 0 });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchMyVerificationCount(districtId)
      .then((count) => {
        if (!cancelled) setState({ status: 'ready', count });
      })
      .catch((e: unknown) => {
        // 失敗しても致命ではないのでログのみ、count=0 として扱う (old2 踏襲)。
        console.error('getMyVerificationCount failed', e);
        if (!cancelled) setState({ status: 'ready', count: 0 });
      });
    return () => {
      cancelled = true;
    };
  }, [districtId, uid]);

  const withdraw = useCallback(async () => {
    if (state.status !== 'ready' || state.count === 0) return;
    setWithdrawing(true);
    setMessage(null);
    try {
      const deleted = await withdrawMyVerifications(districtId);
      // Phase 2 層1: 撤回後に当該地区の **未送信 verification entry を outbox から除去**する。
      // これが無いと、撤回 (server 側 doc 全削除) の後に flush が pending entry を再送して doc を
      // 復活させてしまう (撤回中は outbox を触らないため flush は走らず、この時点で安全に掃除できる)。
      try {
        const pending = await listActions();
        await Promise.all(
          pending
            .filter((e) => e.kind === 'verification' && e.districtId === districtId)
            .map((e) => deleteAction(e.clientId)),
        );
      } catch (cleanupErr) {
        // outbox 掃除の失敗は撤回成功を妨げない (再送されても withdraw 再実行で解消可能)。
        console.error('verification outbox cleanup after withdraw failed', cleanupErr);
      }
      setState({ status: 'ready', count: 0 });
      // old2 逐語: 「{n} 件の検証回答を削除しました。」
      setMessage(`${deleted} 件の検証回答を削除しました。`);
    } catch (e) {
      console.error('withdrawMyVerifications failed', e);
      const msg = e instanceof Error ? e.message : String(e);
      // old2 逐語: 「削除に失敗しました: {msg}」
      setMessage(`削除に失敗しました: ${msg}`);
    } finally {
      setWithdrawing(false);
    }
    // districtName は confirm 文言用に呼び出し側で使う (hook 内では未使用だが API 互換のため受領)。
    void districtName;
  }, [districtId, districtName, state]);

  return { state, withdrawing, message, withdraw };
}
